import React, { useState } from 'react';
import { Training } from '../../../types/profile';
import { EditableSection } from '../common/EditableSection';
import { FormInput } from '../common/FormInput';
import { FormSelect } from '../common/FormSelect';
import { FormTextArea } from '../common/FormTextArea';
import { ListInput } from '../common/ListInput';

interface TrainingSectionProps {
  training: Training[];
  onUpdate: (training: Training[]) => void;
}

interface TrainingFormProps {
  training: Training;
  onChange: (training: Training) => void;
  onRemove: () => void;
}

const TrainingForm: React.FC<TrainingFormProps> = ({ training, onChange, onRemove }) => {
  const handleFieldChange = (field: keyof Training) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    onChange({ ...training, [field]: e.target.value });
  };

  const handleListChange = (items: string[]) => {
    onChange({ ...training, skills_acquired: items });
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 80; year--) {
      years.push(year.toString());
    }
    return years;
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="flex justify-between items-start">
        <h4 className="text-lg font-medium text-gray-900">Training Entry</h4>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-600 hover:text-red-800"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="Training Name"
          name="training_name"
          value={training.training_name}
          onChange={handleFieldChange('training_name')}
          maxLength={100}
        />
        <FormInput
          label="Training Institution"
          name="training_institution"
          value={training.training_institution}
          onChange={handleFieldChange('training_institution')}
          maxLength={100}
        />
        <FormInput
          label="Primary Skill"
          name="skill"
          value={training.skill}
          onChange={handleFieldChange('skill')}
          maxLength={50}
        />
        <FormSelect
          label="Date of Training"
          name="date"
          value={training.date}
          onChange={handleFieldChange('date')}
          options={generateYearOptions()}
          placeholder="Select year"
        />
        <div className="md:col-span-2">
          <ListInput
            label="Secondary Skills Acquired"
            items={training.skills_acquired}
            onItemsChange={handleListChange}
            placeholder="Add skill"
            maxLength={50}
          />
        </div>
        <div className="md:col-span-2">
          <FormTextArea
            label="Description"
            name="description"
            value={training.description}
            onChange={handleFieldChange('description')}
            maxLength={200}
            rows={3}
          />
        </div>
      </div>
    </div>
  );
};

export const TrainingSection: React.FC<TrainingSectionProps> = ({
  training,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTraining, setEditTraining] = useState<Training[]>(training);

  const handleEdit = () => {
    setEditTraining(training);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditTraining(training);
    setIsEditing(false);
  };

  const handleSave = () => {
    onUpdate(editTraining);
    setIsEditing(false);
  };

  const handleAddTraining = () => {
    setEditTraining([
      ...editTraining,
      {
        skill: '',
        training_name: '',
        training_institution: '',
        date: '',
        skills_acquired: [],
        description: ''
      }
    ]);
  };

  const handleUpdateTraining = (index: number) => (train: Training) => {
    const updated = [...editTraining];
    updated[index] = train;
    setEditTraining(updated);
  };

  const handleRemoveTraining = (index: number) => () => {
    setEditTraining(editTraining.filter((_, i) => i !== index));
  };

  return (
    <EditableSection
      title="Training"
      isEditing={isEditing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
    >
      {isEditing ? (
        <div className="space-y-4">
          {editTraining.map((train, index) => (
            <TrainingForm
              key={index}
              training={train}
              onChange={handleUpdateTraining(index)}
              onRemove={handleRemoveTraining(index)}
            />
          ))}
          <button
            type="button"
            onClick={handleAddTraining}
            className="w-full py-2 px-4 border-2 border-dashed border-gray-300 rounded-md text-sm font-medium text-gray-600 hover:border-gray-400 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            + Add Training
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {training.length > 0 ? (
            training.map((train, index) => (
              <div key={index} className="border-b border-gray-200 pb-4 last:border-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{train.training_name}</h3>
                    <p className="text-sm text-gray-600">{train.training_institution} • {train.date}</p>
                  </div>
                  <span className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm font-medium">
                    {train.skill}
                  </span>
                </div>
                {train.skills_acquired.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-1">Skills Acquired:</p>
                    <div className="flex flex-wrap gap-2">
                      {train.skills_acquired.map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 rounded-md text-sm text-gray-700">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {train.description && (
                  <p className="mt-3 text-sm text-gray-600">{train.description}</p>
                )}
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">No training added yet.</p>
          )}
        </div>
      )}
    </EditableSection>
  );
};